/**
 * Client-side rate limiter to prevent rapid-fire form submissions.
 * Tracks attempts per action key and enforces cooldown periods.
 * 
 * Example usage:
 *   import { checkRateLimit } from '../utils/rateLimiter';
 * 
 *   if (!checkRateLimit('password-reset', email)) {
 *     message.error('Too many attempts. Please wait before trying again.');
 *     return;
 *   }
 */

const rateLimitStore = new Map();

/**
 * Check if an action is rate-limited for a given identifier.
 * 
 * @param {string} actionKey - Unique key for the action (e.g., 'password-reset', 'email-signup')
 * @param {string} identifier - User identifier (e.g., email, IP)
 * @param {Object} options - Configuration options
 * @param {number} options.maxAttempts - Maximum attempts allowed (default: 3)
 * @param {number} options.windowMs - Time window in milliseconds (default: 15 minutes)
 * @returns {boolean} true if action is allowed, false if rate-limited
 */
export const checkRateLimit = (
  actionKey,
  identifier,
  { maxAttempts = 3, windowMs = 15 * 60 * 1000 } = {}
) => {
  const key = `${actionKey}:${identifier}`;
  const now = Date.now();

  const record = rateLimitStore.get(key);

  // No previous attempts or window expired
  if (!record || now - record.firstAttempt > windowMs) {
    rateLimitStore.set(key, {
      firstAttempt: now,
      attempts: 1,
    });
    return true;
  }

  // Within window, check attempt count
  if (record.attempts < maxAttempts) {
    record.attempts += 1;
    rateLimitStore.set(key, record);
    return true;
  }

  // Rate limit exceeded
  return false;
};

/**
 * Get remaining time until rate limit resets for an action.
 * 
 * @param {string} actionKey - Unique key for the action
 * @param {string} identifier - User identifier
 * @param {number} windowMs - Time window in milliseconds (default: 15 minutes)
 * @returns {number} Milliseconds until reset, or 0 if not rate-limited
 */
export const getRateLimitReset = (
  actionKey,
  identifier,
  windowMs = 15 * 60 * 1000
) => {
  const key = `${actionKey}:${identifier}`;
  const record = rateLimitStore.get(key);

  if (!record) {
    return 0;
  }

  const elapsed = Date.now() - record.firstAttempt;
  const remaining = windowMs - elapsed;

  return remaining > 0 ? remaining : 0;
};

/**
 * Clear rate limit for a specific action/identifier.
 * 
 * @param {string} actionKey - Unique key for the action
 * @param {string} identifier - User identifier
 */
export const clearRateLimit = (actionKey, identifier) => {
  const key = `${actionKey}:${identifier}`;
  rateLimitStore.delete(key);
};

/**
 * Format milliseconds into human-readable time.
 * 
 * @param {number} ms - Milliseconds
 * @returns {string} Formatted time (e.g., "5 minutes", "30 seconds")
 */
export const formatWaitTime = (ms) => {
  const seconds = Math.ceil(ms / 1000);
  const minutes = Math.floor(seconds / 60);

  if (minutes > 0) {
    return `${minutes} minute${minutes > 1 ? 's' : ''}`;
  }

  return `${seconds} second${seconds > 1 ? 's' : ''}`;
};

// Cleanup old entries periodically (every 5 minutes)
setInterval(() => {
  const now = Date.now();
  const maxAge = 30 * 60 * 1000; // 30 minutes

  for (const [key, record] of rateLimitStore.entries()) {
    if (now - record.firstAttempt > maxAge) {
      rateLimitStore.delete(key);
    }
  }
}, 5 * 60 * 1000);

